import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Trash2, FileText, Package, Users, MapPin, Printer, Download } from 'lucide-react';
import { useComprobante, useDeleteComprobante, useGeneratePDF } from '../../hooks/useComprobantes';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { formatCurrency, formatDate } from '../../lib/utils';
import { toast } from 'sonner';

export default function ComprobanteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const comprobanteId = id ? parseInt(id) : 0;

  const { data: comprobante, isLoading, refetch } = useComprobante(comprobanteId);
  const deleteComprobante = useDeleteComprobante();
  const generatePdfMutation = useGeneratePDF();

  const handleDelete = async () => {
    if (!confirm(`¿Estás seguro de eliminar el comprobante?`)) return;
    try {
      await deleteComprobante.mutateAsync(comprobanteId);
      navigate('/comprobantes');
    } catch (error) {
      toast.error('Error al eliminar el comprobante');
    }
  };

  const handleGeneratePDF = async () => {
    try {
      toast.info('Generando PDF, por favor espera...');
      
      await generatePdfMutation.mutateAsync(comprobanteId);
      toast.success('PDF generado exitosamente');
      refetch(); 
      
    } catch (error) {
      console.error(error);
      toast.error('Error al generar el PDF en el servidor');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Cargando comprobante...</div>
      </div>
    );
  }

  if (!comprobante) {
    return <div className="text-center py-8 text-red-500">Comprobante no encontrado</div>;
  }

  let productos = [];
  try {
    productos = typeof comprobante.productosJson === 'string'
      ? JSON.parse(comprobante.productosJson)
      : comprobante.productosJson || [];
  } catch (e) {
    productos = [];
  }

  const subtotal = productos.reduce((sum: number, p: any) => sum + (Number(p.cantidad) || 1) * (Number(p.precioUnitario) || 0), 0);
  const descuento = (Number(comprobante.promocionPorcentaje) || 0) / 100 * subtotal;
  const total = subtotal - descuento + Number(comprobante.costoEnvio || 0);

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/comprobantes')} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <ArrowLeft size={20} />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-800">
                {comprobante.numeroComprobante || `COMP-${String(comprobante.id).padStart(6, '0')}`}
              </h1>
            </div>
            <p className="text-sm text-gray-500">
              {comprobante.clienteNombre} • {formatDate(comprobante.fechaEmision || comprobante.createdAt)}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {!comprobante.pdfUrl && (
            <Button 
              variant="outline" 
              onClick={handleGeneratePDF}
              disabled={generatePdfMutation.isPending}
            >
              <Printer size={16} className="mr-2" />
              {generatePdfMutation.isPending ? 'Generando...' : 'Generar PDF'}
            </Button>
          )}
          <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={handleDelete}>
            <Trash2 size={16} className="mr-2" />
            Eliminar
          </Button>
        </div>
      </div>

      {/* Grid principal */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          
          {/* Cliente */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-6 flex items-center gap-2">
              <Users size={20} className="text-blue-500" />
              Datos del Cliente
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-8">
              <div>
                <p className="text-sm text-gray-500 mb-1">Nombre</p>
                <p className="font-medium text-gray-800">{comprobante.clienteNombre || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Documento</p>
                <p className="font-medium text-gray-800">{comprobante.clienteDocumento || '-'}</p>
              </div>

              <div>
                <p className="text-sm text-gray-500 mb-1">Localidad</p>
                {comprobante.clienteLocalidad ? (
                  <p className="font-medium text-gray-800 flex items-center gap-1">
                    <MapPin size={14} className="text-gray-400" />
                    {comprobante.clienteLocalidad}
                  </p>
                ) : (
                  <p className="font-medium text-gray-800">-</p>
                )}
              </div>

              <div>
                <p className="text-sm text-gray-500 mb-1">Dirección</p>
                {comprobante.clienteDireccion ? (
                  <a 
                    href={`https://www.google.com/maps/search/${encodeURIComponent(
                      comprobante.clienteDireccion + 
                      (comprobante.clienteLocalidad ? ', ' + comprobante.clienteLocalidad : '')
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-blue-600 hover:underline flex items-center gap-1"
                  >
                    <MapPin size={14} className="text-blue-400" />
                    {comprobante.clienteDireccion}
                  </a>
                ) : (
                  <p className="font-medium text-gray-800">-</p>
                )}
              </div>
            </div>
          </Card>

          {/* Productos */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Package size={20} className="text-blue-500" />
              Productos
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-sm text-gray-500 border-b">
                    <th className="pb-2">Producto</th>
                    <th className="pb-2 text-center">Cantidad</th>
                    <th className="pb-2 text-right">Precio</th>
                    <th className="pb-2 text-right">Subtotal</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {productos.map((producto: any, index: number) => (
                    <tr key={index} className="text-sm">
                      <td className="py-2">
                        <p className="font-medium text-gray-800">{producto.nombre || 'Producto'}</p>
                      </td>
                      <td className="py-2 text-center">{producto.cantidad || 1}</td>
                      <td className="py-2 text-right">{formatCurrency(producto.precioUnitario || 0)}</td>
                      <td className="py-2 text-right">{formatCurrency((producto.cantidad || 1) * (producto.precioUnitario || 0))}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                <tr className="border-t font-bold">
                  <td colSpan={3} className="py-2 text-right">Subtotal Productos</td>
                  <td className="py-2 text-right text-blue-600">{formatCurrency(subtotal)}</td>
                </tr>
              </tfoot>
              </table>
            </div>
          </Card>
        </div>

        {/* Columna derecha */}
        <div className="space-y-6">
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Resumen</h2>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Subtotal</span>
                <span className="font-medium">{formatCurrency(subtotal)}</span>
              </div>
              {comprobante.promocionPorcentaje > 0 && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>Descuento ({comprobante.promocionPorcentaje}%)</span>
                  <span>-{formatCurrency(descuento)}</span>
                </div>
              )}
              {comprobante.costoEnvio > 0 && (
                <div className="flex justify-between text-sm text-blue-600">
                  <span>Costo de Envío</span>
                  <span>+{formatCurrency(comprobante.costoEnvio)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm pt-2 border-t">
                <span className="font-semibold">Total</span>
                <span className="font-bold text-blue-600">{formatCurrency(total)}</span>
              </div>
              {comprobante.observaciones && (
                <div className="pt-2 border-t">
                  <p className="text-sm text-gray-500">Observaciones</p>
                  <p className="text-sm text-gray-600">{comprobante.observaciones}</p>
                </div>
              )}
            </div>
          </Card>

          {/* Información del Sistema */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Información</h2>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Número</span>
                <span className="font-medium">{comprobante.numeroComprobante || `COMP-${String(comprobante.id).padStart(6, '0')}`}</span>
              </div>
              <div className="flex justify-between text-sm pt-2 border-t">
                <span className="text-gray-500">Creado</span>
                <span className="text-xs text-gray-400">{formatDate(comprobante.fechaEmision || comprobante.createdAt)}</span>
              </div>
              {comprobante.updatedAt !== comprobante.createdAt && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Actualizado</span>
                  <span className="text-xs text-gray-400">{formatDate(comprobante.updatedAt)}</span>
                </div>
              )}
            </div>
          </Card>

          {/* PDF */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <FileText size={20} className="text-blue-500" />
              PDF
            </h2>
            <div className="flex flex-col gap-2">
              {comprobante.pdfUrl ? (
                <>
                  <a
                    href={comprobante.pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full py-2 px-4 bg-blue-50 text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
                  >
                    <FileText size={16} />
                    Ver PDF
                  </a>
                  <a
                    href={comprobante.pdfUrl}
                    download
                    className="flex items-center justify-center gap-2 w-full py-2 px-4 bg-green-50 text-green-600 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
                  >
                    <Download size={16} />
                    Descargar PDF
                  </a>
                </>
              ) : (
                <Button
                  onClick={handleGeneratePDF}
                  className="w-full flex items-center justify-center gap-2"
                  variant="outline"
                  disabled={generatePdfMutation.isPending}
                >
                  <Printer size={16} />
                  {generatePdfMutation.isPending ? 'Generando...' : 'Generar PDF'}
                </Button>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}